import { Component, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { isNullOrWhitespace } from 'libs/utils/src/lib/string-helpers/is-null-or-whitespace';
import { pathJoin } from 'libs/utils/src/lib/string-helpers/path-join';
import { BehaviorSubject, forkJoin, interval, merge, Observable, of, Subject } from 'rxjs';
import { switchMap, takeUntil, distinctUntilChanged, first, filter, map } from 'rxjs/operators';
import { DashboardItem } from '../interfaces/dashboard-item.interface';
import { LinkStatus } from '../interfaces/link-status.interface';
import { SelectedCompanyService } from '../selected-company/selected-company.service';
import { DashboardItemService } from '../services/dashboard-item.service';
import { LinkStatusSignalrService } from '../services/signalR/link-status-signalr.service';
import { QueueService } from '../services/queue.service';
import { MarketplaceTileComponent } from './marketplace-tile/marketplace-tile.component';
import { DashboardTileComponent } from './dashboard-tile/dashboard-tile.component';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { FunctionAppsLinkService } from '../services/function-apps-link.service';
import { DashboardContext } from '../interfaces/dashboard-context.interface';
import { CompanyAccessRequestModalComponent } from './company-access-request-modal/company-access-request-modal.component';
import { SubcompanyDashboardTileComponent } from './subcompany-dashboard-tile/subcompany-dashboard-tile.component';
import { AlCompanyService } from '../services/al-company.service';
import { showErrorSwal } from '@asamco/utils';
import Swal from 'sweetalert2';
import { UserCompanyAccessRequest } from '../interfaces/user-company-access-request.interface';


interface DashboardContextWithFunctionAppData extends DashboardContext {
	functionAppData?: DashboardItem[];
}

@Component({
	selector: 'al-dashboard',
	templateUrl: './dashboard.component.html',
	styleUrls: ['./dashboard.component.scss', '../styles/tile-style.scss'],
	standalone: true,
	imports: [
		NgIf,
		NgFor,
		DashboardTileComponent,
		SubcompanyDashboardTileComponent,
		MarketplaceTileComponent,
		CompanyAccessRequestModalComponent,
		AsyncPipe,
	],
})
export class DashboardComponent implements OnInit, OnDestroy {
	/** Observable with dashboard items */
	private readonly destroy$ = new Subject<void>();

	public data$: BehaviorSubject<DashboardContextWithFunctionAppData> = new BehaviorSubject<DashboardContextWithFunctionAppData>(undefined);

	public imgUrl = '';
	public imagename = 'applicationink-logo.png';

	@ViewChildren(DashboardTileComponent)
	tileComponents: QueryList<DashboardTileComponent>;

	@ViewChild(CompanyAccessRequestModalComponent)
	accessRequestModal: CompanyAccessRequestModalComponent;

	public linkStatus$: Observable<LinkStatus>;
	constructor(
		private dashboardItemService: DashboardItemService,
		private router: Router,
		private companyService: AlCompanyService,
		private linkStatusSignalRService: LinkStatusSignalrService,
		private selectedCompanyService: SelectedCompanyService,
		private queueService: QueueService,
		private functionAppsLinkService: FunctionAppsLinkService,
	) {
		this.imgUrl = pathJoin([window['deployUrl'], '/assets/img/']);
	}

	getFunctionAppInfoData(dashboardItems: DashboardItem[], companyId: number) {
		return forkJoin([
			this.functionAppsLinkService.GetCustomLinkConfigByCompany(companyId),
			this.queueService.areLinksRunning(dashboardItems.map(({ id }) => id)),
		]).pipe(
			switchMap(([functionAppInfos, isLinkRunningObjects]) => {
				const updateItems = dashboardItems.map(item => {
					const functionAppInfo = functionAppInfos.find(info => item.id === info.linkId);
					if (functionAppInfo) {
						item.functionAppInfo = {
							globalApp: functionAppInfos.find(info => info.originType === 'Global' && info.linkId === item.id),
							entryPointApp: functionAppInfos.find(info => info.originType === 'Entrypoint' && info.linkId === item.id),
							sourceApp: functionAppInfos.find(info => info.originType === 'Source' && info.linkId === item.id),
							targetApp: functionAppInfos.find(info => info.originType === 'Target' && info.linkId === item.id),
						};
					}

					const isLinkRunningObject = isLinkRunningObjects.find(obj => item.id === obj.linkId);
					if (isLinkRunningObject) {
						item.isLinkRunning = isLinkRunningObject.isLinkRunning;
					}

					return item;
				});

				return of(updateItems);
			})
		);
	}

	ngOnInit(): void {
		this.linkStatus$ = this.linkStatusSignalRService.getLinkStatusObservable();
		// functionAppInfoService.getDetailedInfoById depends on the selected CompanyId. We
		// want to trigger this logic when it changes.

		this.initDashboard();
	}

	initDashboard() {
		//We use this observable so we can keep track of when we're switching companies (in admin mode)
		this.selectedCompanyService
			.getCompanyIdObservable()
			.pipe(
				distinctUntilChanged(),
				filter(companyId => ![-1, -2, null].includes(companyId)),
				switchMap((companyId: number) => {
					return this.dashboardItemService.getContext();
				}),
				takeUntil(this.destroy$)
			)
			.subscribe({
				next: contextFromService => {
					// TODO: This typing is odd. We should refactor it to be more clear.
					const context: DashboardContextWithFunctionAppData = contextFromService;

					if (context.currentAccessRequest || !context.isValidCompany) {
						this.data$.next(context);
					} else if (context.isMainCompany) {
						for (const company of context.subCompanies) {
							company.userHasAccess = context.usersCompanyIds.includes(company.id);
						}

						this.data$.next(context);
					} else {
						this.data$.next(context);

						merge(this.tileComponents.changes.pipe(first()), interval(10000))
							.pipe(takeUntil(this.destroy$))
							.subscribe(() => {
								const currentTime = new Date().getTime();
								this.tileComponents.forEach(tc => tc.isLinkExpiredSubject.next(tc.expiryTime < currentTime));
							});

						this.getFunctionAppInfoData(context.links, context.companyId).subscribe(fad => {
							context.functionAppData = fad;
							this.data$.next(context);
						});
					}
				},
				error: error => {
					showErrorSwal(error);
					this.data$.next(<any>{ isValidCompany: false });
				},
			});
	}

	retractAccessRequest() {
		this.companyService.handleAccessRequest(this.data$.value.currentAccessRequest.requestId, false).subscribe({
			next: r => {
				Swal.fire('Access request retracted', '', 'success');
				this.initDashboard();
			},
			error: showErrorSwal,
		});
	}

	onAccessRequested() {
		this.initDashboard();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	public getUrl(subUrl: string): string {
		if (!isNullOrWhitespace(this.imgUrl) && !isNullOrWhitespace(subUrl)) {
			return pathJoin([this.imgUrl, subUrl]);
		}
		return '';
	}

	/** Views the link based on the link ID of the clicked card */
	public viewApplication(e, id: number) {
		this.router.navigateByUrl('application-link/link/' + id);
	}
}
function foreach(functionAppInfo: any[], arg1: (info: any) => void, arg2: void) {
	throw new Error('Function not implemented.');
}
